import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import { api, photoUrl, errText } from "../../lib/api";
import { renderBanner, canvasToBlob, BANNER_STYLES } from "../../lib/bannerRender";

const LOGO_OPTS = [["profile", "My profile photo"], ["upload", "Upload a logo"], ["none", "No logo"]];
const SWATCHES = [["#FFFFFF", "White"], ["#12234A", "Navy"], ["#F5551E", "Orange"], ["#FFC233", "Gold"], ["#0F172A", "Black"], ["#10B981", "Green"]];

const SizeSlider = ({ label, value, onChange, min, max, id, disabled }) => (
  <div className={disabled ? "opacity-50" : ""}>
    <div className="flex items-center justify-between mb-1.5">
      <p className="text-xs font-semibold text-slate-600">{label}</p>
      <button type="button" onClick={() => onChange(1)} disabled={disabled} className="text-xs text-slate-400 hover:text-[#F5551E]" data-testid={`${id}-reset`}>{Math.round(value * 100)}% · Reset</button>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-xs font-bold text-slate-400">A</span>
      <Slider value={[value]} min={min} max={max} step={0.05} disabled={disabled} onValueChange={(v) => onChange(v[0])} className="flex-1" data-testid={`${id}-slider`} />
      <span className="text-lg font-bold text-slate-500">A</span>
    </div>
  </div>
);

export const BannerTemplateDialog = ({ open, onClose, data, onSaved }) => {
  const [tpls, setTpls] = useState([]);
  const [sel, setSel] = useState(null);
  const [name, setName] = useState(data.name || "");
  const [tagline, setTagline] = useState(data.tagline || "");
  const [logoMode, setLogoMode] = useState(data.picture ? "profile" : "none");
  const [logoFile, setLogoFile] = useState(null);
  const [style, setStyle] = useState("bold");
  const [color, setColor] = useState(null);
  const [scale, setScale] = useState(1);
  const [tagScale, setTagScale] = useState(1);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/banner-templates").then((r) => { setTpls(r.data); setSel((s) => s || r.data[0] || null); }).catch((e) => toast.error(errText(e)));
  }, [open]);

  const logo = logoMode === "profile" ? photoUrl(data.picture) : logoMode === "upload" ? logoFile : null;
  const opts = sel && { bg: photoUrl(sel.image), color: color || sel.text_color, align: sel.align, shade: sel.shade, name: name.trim(), tagline: tagline.trim(), logo, style, scale, tagScale };

  useEffect(() => {
    if (!opts) return undefined;
    let on = true;
    const t = setTimeout(() => renderBanner(opts).then((c) => on && setPreview(c.toDataURL("image/jpeg", 0.7))).catch(() => on && setPreview("")), 250);
    return () => { on = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, name, tagline, logo, style, color, scale, tagScale]);

  const pickLogo = (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f || !f.type.startsWith("image/")) return;
    setLogoFile(URL.createObjectURL(f)); setLogoMode("upload");
  };

  const save = async () => {
    if (!opts) return;
    setSaving(true);
    try {
      const blob = await canvasToBlob(await renderBanner(opts));
      const fd = new FormData();
      fd.append("file", new File([blob], "banner.jpg", { type: "image/jpeg" }));
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/me/profile-page", { cover_image: up.path });
      toast.success("Banner saved");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.name === "SecurityError" ? "Your profile photo can't be used here. Upload a logo instead." : errText(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-4xl bg-white max-h-[92vh] overflow-y-auto" data-testid="banner-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">Make a banner from a template</DialogTitle>
          <DialogDescription className="text-slate-500">Pick a design, add your name and logo, then save. It's made at 2000 × 260 px so it fills the frame.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl overflow-hidden bg-slate-900 aspect-[100/13]" data-testid="banner-template-preview">
          {preview ? <img src={preview} alt="Banner preview" className="w-full h-full object-contain" /> : <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-white/60" /></div>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="banner-template-list">
          {tpls.map((t) => (
            <button key={t.id} type="button" onClick={() => setSel(t)} data-testid={`banner-template-${t.id}`}
              className={`relative rounded-lg overflow-hidden border-2 transition-colors ${sel?.id === t.id ? "border-[#F5551E]" : "border-transparent hover:border-slate-300"}`}>
              <img src={photoUrl(t.image)} alt={t.name} className="w-full aspect-[100/13] object-cover" />
              <span className="block text-[11px] font-semibold text-[#12234A] text-left px-1.5 py-1 truncate">{t.name}</span>
              {sel?.id === t.id && <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-[#F5551E] text-white flex items-center justify-center"><Check size={12} /></span>}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-xs font-semibold text-slate-600">Name on banner</span>
            <Input value={name} maxLength={50} onChange={(e) => setName(e.target.value)} data-testid="banner-template-name" className="mt-1" /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-600">Tagline (optional)</span>
            <Input value={tagline} maxLength={80} onChange={(e) => setTagline(e.target.value)} placeholder="e.g. CKC-registered puppies near Ottawa" data-testid="banner-template-tagline" className="mt-1" /></label>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Text style</p>
          <div className="flex flex-wrap gap-2" data-testid="banner-style-options">
            {Object.entries(BANNER_STYLES).map(([k, v]) => (
              <button key={k} type="button" onClick={() => setStyle(k)} data-testid={`banner-style-${k}`}
                className={`h-11 px-4 rounded-xl border inline-flex items-center gap-2 transition-colors ${style === k ? "border-[#F5551E] bg-orange-50 text-[#12234A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <span className="text-lg leading-none" style={{ fontFamily: v.name[1], fontWeight: v.name[0] }}>Aa</span>
                <span className="text-sm font-semibold">{v.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <SizeSlider label="Name size" value={scale} onChange={setScale} min={0.6} max={1.5} id="banner-size" />
          <SizeSlider label="Tagline size" value={tagScale} onChange={setTagScale} min={0.6} max={1.6} id="banner-tag-size" disabled={!tagline.trim()} />
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Text colour</p>
          <div className="flex flex-wrap items-center gap-2" data-testid="banner-color-options">
            <button type="button" onClick={() => setColor(null)} data-testid="banner-color-default"
              className={`h-9 px-3 rounded-xl border text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${!color ? "border-[#F5551E] bg-orange-50 text-[#12234A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              <span className="h-4 w-4 rounded-full border border-slate-300" style={{ background: sel?.text_color || "#FFFFFF" }} /> Template default
            </button>
            {SWATCHES.map(([hex, label]) => (
              <button key={hex} type="button" onClick={() => setColor(hex)} title={label} aria-label={label} data-testid={`banner-color-${hex.slice(1)}`}
                className={`h-9 w-9 rounded-full border-2 transition-transform hover:scale-110 ${color === hex ? "border-[#F5551E] ring-2 ring-orange-200" : "border-slate-200"}`} style={{ background: hex }} />
            ))}
            <label className={`h-9 px-3 rounded-xl border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer ${color && !SWATCHES.some(([h]) => h === color) ? "border-[#F5551E] bg-orange-50 text-[#12234A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              <input type="color" value={color || sel?.text_color || "#FFFFFF"} onChange={(e) => setColor(e.target.value.toUpperCase())} className="h-5 w-6 cursor-pointer bg-transparent" data-testid="banner-color-custom" /> Custom
            </label>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Logo</p>
          <div className="flex flex-wrap gap-2">
            {LOGO_OPTS.filter(([v]) => v !== "profile" || data.picture).map(([v, label]) => (
              <button key={v} type="button" data-testid={`banner-logo-${v}`}
                onClick={() => (v === "upload" ? (logoFile && logoMode !== "upload" ? setLogoMode("upload") : fileRef.current?.click()) : setLogoMode(v))}
                className={`h-9 px-3.5 rounded-xl border text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${logoMode === v ? "border-[#F5551E] bg-orange-50 text-[#12234A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {v === "upload" && <ImagePlus size={14} />} {label}
              </button>
            ))}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="banner-logo-file" />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-xl" data-testid="banner-template-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !sel} data-testid="banner-template-save" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
            {saving && <Loader2 className="animate-spin" size={16} />} Use this banner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
